import { createReducer, on } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';
import { PagedGrid, PageInfo, VisibleRangeModel } from '../../../models';
import { SubRowType } from '../../../models/grid-row.model';
import { calculateChunkOffset, calculatePageInfo } from '../helpers';
import { PagedGridActions } from '../paged-grid.actions';

export interface PagedGridState {
  gridId: string;
  initialized: boolean;
  enableChunksCaching: boolean;
  columnsData: PagedGrid.ColumnData[];
  chunks: PagedGrid.Chunk[];
  computedSubRows: PagedGrid.BaseGridChunkSubRowMetadata[];
  viewId: string;
  visibleRange: VisibleRangeModel;
  pageInfo: PageInfo;
  expandedRowIds: Record<string, number>;
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  chunksBuffer: number;
  /**
   * The property is used to set into true at first finish of any chunk is loaded. Can be used to determine finish phase of initialization of grid data.
   */ 
  chunkLoaded: boolean;
}

const pageInfoInitialState: PageInfo = {
  rowsPerPage: 100000,
  rowsPerChunk: 1000,
  totalPages: 0,
  totalRows: 0,
  currentPageIndex: 0,
  rowChunkSize: 0,
  maxChunksInCurrentPage: 0,
  loadedCount: 0
};

const initialState: PagedGridState = {
  gridId: null,
  initialized: false,
  enableChunksCaching: false,
  columnsData: null,
  chunks: [],
  computedSubRows: null,
  visibleRange: null,
  pageInfo: null,
  expandedRowIds: {},
  sortColumn: null,
  sortDirection: null,
  viewId: null,
  chunksBuffer: 1,
  chunkLoaded: false,
};

export const gridInstanceReducer = createReducer(
  initialState,
  on(PagedGridActions.Init, (state, action) => {
    const newState: PagedGridState = {
      ...initialState,
      initialized: true,
      gridId: action.instanceId,
      enableChunksCaching: action.enableChunkCaching,
      pageInfo: {
        ...pageInfoInitialState,
        rowsPerChunk: action.chunkSize,
        rowChunkSize: action.rowChunkSize,
        rowsPerPage: action.pageSize,
      },
      viewId: uuidv4(),
      chunks: [],
    };

    computeDataInChunks({
      chunks: newState.chunks,
      rowChunkSize: newState.pageInfo.rowChunkSize,
      expandedRowIds: state.expandedRowIds,
    });

    return newState;
  }),
  on(PagedGridActions.SetTotalCount, (state, action) => {
    const newState: PagedGridState = {
      ...state,
      pageInfo: calculatePageInfo({
        ...state.pageInfo,
        totalRows: action.totalCount,
      }),
    };

    return newState;
  }),
  on(PagedGridActions.SetColumnsData, (state, action) => {
    return {
      ...state,
      columnsData: action.columnsData,
    };
  }),
  on(PagedGridActions.Dispose, () => {
    return {
      ...initialState,
    };
  }),
  on(PagedGridActions.SelectPage, (state, action) => {
    return {
      ...state,
      chunks: [],
      subRows: [],
      pageInfo: calculatePageInfo({
        ...state.pageInfo,
        currentPageIndex: action.pageIndex,
        loadedCount: 0
      }),
    };
  }),
  on(PagedGridActions.CleanNotVisibleChunks, (state, action) => {
    if (!state.chunks[action.visibleRange.startChunkIndex]) {
      return state;
    }

    if (
      state.visibleRange?.startChunkIndex != action.visibleRange.startChunkIndex ||
      state.visibleRange?.endChunkIndex != action.visibleRange.endChunkIndex
    ) {
      const startIndex = Math.max(action.visibleRange.startChunkIndex - state.chunksBuffer - 1, 0);
      const endIndex = action.visibleRange.endChunkIndex + state.chunksBuffer + 1;

      const aboveChunks = state.chunks.slice(0, startIndex);
      const belowChunks = state.chunks.slice(endIndex);

      [aboveChunks, belowChunks].flat().forEach((chunk) => {
        chunk.rows.forEach((row) => {
          row.loading = true;

          row.displayedSubRows.forEach((subRow) => {
            subRow.loading = true;
            if (subRow.subRowType === SubRowType.Data) {
              delete (subRow as PagedGrid.GridChunkSubRow).subRowData;
            }
          });
          row.fullSubRows = null;
        });

        state.chunks[chunk.index] = {
          ...chunk,
          loaded: false,
          loading: false,
          loadingIndicator: true,
        };
      });

      state.visibleRange = action.visibleRange;
    }

    return state;
  }),
  on(PagedGridActions.ChunkLoaded, (state) => {
    return  {
      ...state,
      chunkLoaded: true
    };
  }),
  on(PagedGridActions.LoadChunk, (state, action) => {
    const newState: PagedGridState = {
      ...state,
    };

    if (newState.chunks[action.chunkIndex]) {
      newState.chunks[action.chunkIndex] = {
        ...newState.chunks[action.chunkIndex],
        loaded: false,
        loading: true,
        loadingIndicator: true,
      };
    }

    return newState;
  }),
  on(PagedGridActions.SetChunk, (state, action) => {
    const newState: PagedGridState = {
      ...state,
      pageInfo: {
        ...state.pageInfo,
        loadedCount: state.pageInfo.loadedCount + action.rows.length,
      },
      viewId: 'data-view',
    };

    const chunkRowsMetadata: PagedGrid.GridChunkRowMetadata[] = [];

    action.rows.forEach((row, rowIndexInChunk) => {
      chunkRowsMetadata.push({
        rowId: row.rowId,
        rowIndex: undefined, // TODO
        fullSubRows: row.subRows,
        rowIndexInChunk,
        displayedSubRows: row.subRows.map(
          (subrow) =>
            ({
              rowId: row.rowId,
              subRowData: subrow.subRowData,
              rowIndexInChunk,
            }) as PagedGrid.BaseGridChunkSubRowMetadata,
        ),
      });
    });

    newState.chunks = [...state.chunks];
    const chunksForHandling = [];

    newState.chunks[action.chunkIndex] = {
      index: action.chunkIndex,
      rows: chunkRowsMetadata,
      loaded: true,
      loading: false,
      loadingIndicator: false,
    };
    chunksForHandling.push(newState.chunks[action.chunkIndex]);

    defineChunks(newState);

    computeDataInChunks({
      chunks: chunksForHandling,
      rowChunkSize: newState.pageInfo.rowChunkSize,
      expandedRowIds: newState.expandedRowIds,
    });

    return newState;
  }),
  on(PagedGridActions.ShowMoreInRow, (state, action) => {
    const newValue = (state.expandedRowIds[action.rowIndex] || 0) + 1;
    const newState = {
      ...state,
      expandedRowIds: {
        ...state.expandedRowIds,
        [action.rowIndex]: newValue,
      },
    };

    computeDataInChunks({
      chunks: [newState.chunks[action.chunkIndex]],
      rowChunkSize: newState.pageInfo.rowChunkSize,
      expandedRowIds: newState.expandedRowIds,
    });

    return newState;
  }),
  on(PagedGridActions.ShowLessInRow, (state, action) => {
    if (state.expandedRowIds[action.rowIndex] > 0) {
      state.expandedRowIds[action.rowIndex] = state.expandedRowIds[action.rowIndex] - 1;
    } else {
      delete state.expandedRowIds[action.rowIndex];
    }

    const newState = {
      ...state,
      expandedRowIds: {
        ...state.expandedRowIds,
      },
    };

    computeDataInChunks({
      chunks: [newState.chunks[action.chunkIndex]],
      rowChunkSize: newState.pageInfo.rowChunkSize,
      expandedRowIds: newState.expandedRowIds,
    });

    return newState;
  }),
  on(PagedGridActions.ExpandRow, (state, action) => {
    const allSubrowsCount = state.chunks[action.chunkIndex].rows[action.rowIndexInChunk].fullSubRows.length;
    
    const displayedSubrowChunks = Math.ceil(allSubrowsCount / state.pageInfo.rowChunkSize) - 1;

    const newState = {
      ...state,
      expandedRowIds: {
        ...state.expandedRowIds,
        [action.rowIndex]: displayedSubrowChunks,
      },
    };

    computeDataInChunks({
      chunks: [newState.chunks[action.chunkIndex]],
      rowChunkSize: newState.pageInfo.rowChunkSize,
      expandedRowIds: newState.expandedRowIds,
    });

    return newState;
  }),
  on(PagedGridActions.CollapseRow, (state, action) => {
    delete state.expandedRowIds[action.rowIndex];

    const newState = {
      ...state,
      expandedRowIds: {
        ...state.expandedRowIds,
      },
    };

    computeDataInChunks({
      chunks: [newState.chunks[action.chunkIndex]],
      rowChunkSize: newState.pageInfo.rowChunkSize,
      expandedRowIds: newState.expandedRowIds,
    });

    return newState;
  }),
  on(PagedGridActions.ChangeSortColumn, (state, action) => {
    if (!state.columnsData.find((columnData) => columnData.columnId === action.columnName)?.sortable) {
      return state;
    }

    let sortDirection = state.sortDirection;

    if (state.sortColumn === action.columnName) {
      sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      sortDirection = 'asc';
    }

    let chunks = state.chunks;

    if (chunks.length) {
      const loadingChunk = state.chunks[0];
      loadingChunk.loaded = false;
      loadingChunk.loading = true;
      loadingChunk.loadingIndicator = true;
      chunks = [loadingChunk];
    }

    return {
      ...state,
      sortColumn: action.columnName,
      sortDirection,
      chunks,
      pageInfo: {
        ...state.pageInfo,
        loadedCount: 0
      }
    };
  }),
  on(PagedGridActions.Reload, (state) => {
    let chunks = state.chunks;

    if (chunks.length) {
      const loadingChunk = state.chunks[0];
      loadingChunk.loaded = false;
      loadingChunk.loading = true;
      loadingChunk.loadingIndicator = true;
      chunks = [loadingChunk];
    }

    return {
      ...state,
      chunks,
      pageInfo: {
        ...state.pageInfo,
        totalRows: 0,
        loadedCount: 0
      },
      viewId: uuidv4(),
    };
  }),
);

function defineChunks(state: PagedGridState): void {
  if (state.chunks.length === state.pageInfo.maxChunksInCurrentPage) {
    return;
  }

  const chunksToCompute: PagedGrid.Chunk[] = [];

  for (let chunkIndex = 0; chunkIndex < state.pageInfo.maxChunksInCurrentPage; chunkIndex++) {
    if (chunkIndex in state.chunks) {
      continue;
    }

    const loadingChunk = createLoadingChunk(state);

    if (loadingChunk.rows.length) {
      loadingChunk.index = chunkIndex;
      state.chunks[loadingChunk.index] = loadingChunk;
      chunksToCompute.push(loadingChunk);
    }
  }

  computeDataInChunks({
    chunks: chunksToCompute,
    rowChunkSize: state.pageInfo.rowChunkSize,
    expandedRowIds: state.expandedRowIds,
  });
}

function createLoadingChunk(state: PagedGridState): PagedGrid.Chunk {
  const loadingChunkIndex = state.chunks.length;
  const sampleChunk = state.chunks[0];

  const count = Math.min(sampleChunk.rows.length, state.pageInfo.totalRows - state.pageInfo.loadedCount);
  const chunkOffset = calculateChunkOffset({
    chunkIndex: loadingChunkIndex,
    currentPageIndex: state.pageInfo.currentPageIndex,
    rowsPerChunk: state.pageInfo.rowsPerChunk,
    rowsPerPage: state.pageInfo.rowsPerPage,
  });
  const result: PagedGrid.Chunk = {
    index: state.chunks.length,
    rows: new Array(count).fill(0).map((_, index) => {
      const rowId = `loading-${chunkOffset + index}`;
      const rowIndex = chunkOffset + index;

      return {
        rowId,
        rowIndex,
        loading: true,
        fullSubRows: [
          {
            subRowData: {},
          } as PagedGrid.GridChunkSubRow
        ],
      } as PagedGrid.GridChunkRowMetadata;
    }),
    loaded: false,
    loading: false,
    loadingIndicator: true,
  };

  return result;
}

function computeDataInChunks(params: { chunks: PagedGrid.Chunk[]; rowChunkSize: number; expandedRowIds }): void {
  const expansionChunkSize = params.rowChunkSize;

  params.chunks.forEach((chunk, chunkIndex) => {
    chunk.rows?.forEach((row) => {
      row.loading = chunk.loadingIndicator;
      row.chunkIndex = chunk.index;
      row.displayedSubRows = [];
      const maxVisibleItems = ((params.expandedRowIds[row.rowIndex] || 0) + 1) * expansionChunkSize;

      const rowRelatedSubRows = row.fullSubRows
        .filter((_, index) => index + 1 <= maxVisibleItems)
        .map((subRow) => {
          return {
            chunkIndex: chunkIndex,
            rowIndex: row.rowIndex,
            rowId: row.rowId,
            loading: row.loading,
            subRowType: PagedGrid.SubRowType.Data,
            subRowData: subRow.subRowData,
          } as PagedGrid.BaseGridChunkSubRowMetadata;
        });
      row.displayedSubRows.push(...rowRelatedSubRows);

      if (row.fullSubRows.length > expansionChunkSize) {
        row.displayedSubRows.push({
          rowId: row.rowId,
          rowIndex: row.rowIndex,
          subRowType: PagedGrid.SubRowType.ExpandCollapse,
          chunkIndex: chunk.index,
          loading: row.loading,
          displayedChunksCount: Math.ceil(rowRelatedSubRows.length / expansionChunkSize),
          hiddenChunksCount: Math.ceil((row.fullSubRows.length - rowRelatedSubRows.length) / expansionChunkSize),
        } as PagedGrid.ExpandCollapseRow);
      }
    });
  });
}
